import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { appToast } from '../../../components/AppToast'
import { getStudentDetail } from './api/studentsApi'
import { getReportTerms, sendReportEmail } from './api/studentScreensApi'

export default function StudentSendReportEmailScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const studentId = searchParams.get('id')

  const [student, setStudent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSending, setIsSending] = useState(false)
  const [formData, setFormData] = useState({
    recipientEmail: '',
    recipientName: '',
    term: '',
    includeMarks: true,
    message: '',
  })
  const [reportTerms, setReportTerms] = useState([])

  useEffect(() => {
    loadStudent()
    loadReportTerms()
  }, [studentId])

  const loadStudent = async () => {
    try {
      const data = await getStudentDetail(studentId)
      setStudent(data)
      const recipientEmail = data?.gEmail || data?.email || ''
      setFormData((prev) => ({
        ...prev,
        recipientEmail,
        recipientName: data?.gName || (data ? `${data.firstName || ''} ${data.lastName || ''}`.trim() : ''),
      }))
    } catch (err) {
      appToast.error('Failed to load student')
    } finally {
      setIsLoading(false)
    }
  }

  const loadReportTerms = async () => {
    try {
      const terms = await getReportTerms(studentId)
      setReportTerms(terms || [])
      if (terms && terms.length > 0) {
        setFormData((prev) => ({ ...prev, term: terms[0].id }))
      }
    } catch (err) {
      console.error('Failed to load terms')
    }
  }

  const goBack = () => navigate('/admission/students/list')

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.recipientEmail) {
      appToast.warning('Please enter recipient email')
      return
    }

    if (!formData.term) {
      appToast.warning('Please select a report term')
      return
    }

    try {
      setIsSending(true)
      const selected = reportTerms.find((t) => t.id === formData.term) || {}
      await sendReportEmail(studentId, {
        recipientEmail: formData.recipientEmail,
        recipientName: formData.recipientName,
        term: formData.term,
        termLabel: selected.term || formData.term,
        includeMarks: formData.includeMarks,
        message: formData.message,
      })
      appToast.success('Report card email sent successfully')
    } catch (err) {
      appToast.error('Failed to send email')
    } finally {
      setIsSending(false)
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-6 pb-8">
      {/* Header */}
      <section className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Admission</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Students</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Send Report Card Email</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-on-surface">
            {student ? `${student.firstName} ${student.lastName}` : 'Student'} - Send Report Card
          </h1>
        </div>
        <button
          onClick={goBack}
          className="inline-flex items-center gap-2 rounded-xl bg-secondary-container px-6 py-3 text-sm font-bold text-secondary hover:bg-secondary-container/80"
        >
          <span className="material-symbols-outlined text-base">arrow_back</span>
          Back to Students
        </button>
      </section>

      {isLoading ? (
        <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-low px-6 py-12 text-center">
          <span className="material-symbols-outlined animate-spin text-3xl text-primary/40">progress_activity</span>
          <p className="mt-3 text-sm text-on-surface-variant">Loading...</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="rounded-2xl border border-outline-variant/30 bg-surface-container-low p-8 shadow-xs">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Recipient Email */}
            <label className="grid gap-1.5">
              <span className="text-sm font-bold text-on-surface">Recipient Email *</span>
              <input
                type="email"
                value={formData.recipientEmail}
                onChange={(e) => setFormData({ ...formData, recipientEmail: e.target.value })}
                placeholder="parent@example.com"
                className="rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>

            {/* Recipient Name */}
            <label className="grid gap-1.5">
              <span className="text-sm font-bold text-on-surface">Recipient Name</span>
              <input
                type="text"
                value={formData.recipientName}
                onChange={(e) => setFormData({ ...formData, recipientName: e.target.value })}
                placeholder="Name"
                className="rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
            </label>

            {/* Term Selection */}
            <label className="grid gap-1.5">
              <span className="text-sm font-bold text-on-surface">Report Term *</span>
              <select
                value={formData.term}
                onChange={(e) => setFormData({ ...formData, term: e.target.value })}
                className="rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
              >
                <option value="">Select Term</option>
                {reportTerms.map((term) => (
                  <option key={term.id} value={term.id}>
                    {term.term}
                  </option>
                ))}
              </select>
            </label>

            {/* Include Marks */}
            <label className="flex items-center gap-3 rounded-lg border border-outline-variant/30 bg-surface-container-lowest p-3">
              <input
                type="checkbox"
                checked={formData.includeMarks}
                onChange={(e) => setFormData({ ...formData, includeMarks: e.target.checked })}
                className="h-4 w-4 rounded border-outline-variant text-primary"
              />
              <span className="text-sm font-medium text-on-surface">Include Marks in Email</span>
            </label>
          </div>

          {/* Message */}
          <label className="mt-6 grid gap-1.5">
            <span className="text-sm font-bold text-on-surface">Message (Optional)</span>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              placeholder="Add any additional message..."
              className="min-h-[120px] rounded-lg border border-outline-variant/30 bg-surface-container-lowest px-4 py-3 text-sm outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
            />
          </label>

          {/* Preview Note */}
          <div className="mt-6 rounded-lg bg-info/10 p-4">
            <p className="text-sm text-info">
              <span className="material-symbols-outlined mr-2 inline text-base">info</span>
              Email will be sent to <strong>{formData.recipientEmail}</strong> with the selected report card for{' '}
              <strong>{reportTerms.find((t) => t.id === formData.term)?.term || 'selected term'}</strong>
            </p>
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex gap-3 justify-end">
            <button
              type="button"
              onClick={goBack}
              className="rounded-lg bg-surface-variant px-6 py-2.5 text-sm font-bold text-on-surface-variant hover:bg-outline-variant/50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSending}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-2.5 text-sm font-bold text-on-primary hover:bg-primary/90 disabled:opacity-60"
            >
              <span className="material-symbols-outlined text-base">mail</span>
              {isSending ? 'Sending...' : 'Send Email'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}
